import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
// The ring glyph, not MUI's confusingly-named `HelpOutlined` — that one is
// the *Outlined family's* version of the filled `Help`, and draws a solid
// disc that pulls more attention than the nav items above it, backwards for
// a de-emphasised support link.
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import PrivacyNoticeIcon from '@mui/icons-material/PolicyOutlined';
import { Link, useLocation } from 'react-router-dom';
import { FOCUS_RING } from '../theme/tokens';
import { numericSx } from '../theme/typography';
import { ABOUT_COPY } from '../components/about/copy';
import { readBuildInfo, UNKNOWN } from '../components/about/buildInfo';
import { type NavSection } from './nav';
import { DRAWER_WIDTH, SUPPORT_MAILTO } from './constants';
import { usePrivacyNotice } from '../components/privacy/hooks/usePrivacyNotice';
import { EXTERNAL_LINK_PROPS, PRIVACY_NOTICE_LABEL } from '../components/privacy/PrivacyNoticeLink';

const drawerWidth = DRAWER_WIDTH;

interface DrawerContentProps {
  collapsed: boolean;
  navSections: NavSection[];
  onCollapseToggle: () => void;
  onNavClick: (e: React.MouseEvent, path: string) => void;
}

function BottomLink({
  icon,
  label,
  href,
  badge,
  collapsed,
  external = false,
}: {
  icon: React.ReactElement;
  label: string;
  href: string;
  badge?: string;
  collapsed: boolean;
  /** Opens in a new tab, and says so to assistive tech. */
  external?: boolean;
}) {
  const theme = useTheme();
  // A real anchor, named even when collapsed hides the text — the icon alone is
  // not a name, and a tooltip is not a substitute for one.
  const inner = (
    <Box
      component="a"
      href={href}
      aria-label={external ? `${label} (opens in a new tab)` : label}
      {...(external ? EXTERNAL_LINK_PROPS : {})}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        px: '10px',
        // Matched to the nav items above, so a real link reads as their peer.
        py: '7px',
        minHeight: 36,
        borderRadius: 1,
        cursor: 'pointer',
        // `text.secondary`, matching the nav items — not the `text.disabled`
        // this carried as a decorative placeholder, which measures 3.7:1 on the
        // sidebar surface and fails AA for a control you can now focus and
        // follow.
        color: 'text.secondary',
        textDecoration: 'none',
        fontSize: 12,
        justifyContent: collapsed ? 'center' : 'flex-start',
        '&:hover': { bgcolor: theme.palette.accent.tint },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: FOCUS_RING(alpha(theme.palette.primary.main, 0.35)),
        },
      }}
    >
      <Box component="span" sx={{ fontSize: 14, display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      {!collapsed && (
        <>
          <Typography component="span" sx={{ fontSize: 12 }}>{label}</Typography>
          {badge && (
            <Box
              component="span"
              sx={{
                ml: 'auto',
                fontSize: 11.5,
                fontWeight: 700,
                px: '6px',
                py: '1px',
                borderRadius: 999,
                bgcolor: theme.palette.accent.tint,
                color: 'primary.main',
                lineHeight: 1.6,
              }}
            >
              {badge}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  // The tooltip wraps the anchor itself, so it also opens on keyboard focus.
  return collapsed ? (
    <Tooltip title={label} placement="right" arrow>
      {inner}
    </Tooltip>
  ) : (
    inner
  );
}

/**
 * The build's version, quietly, under Help & support: the sidebar's way into
 * the About page. Without a known version it reads "About" rather than
 * "unknown". Navigation runs through `onNavClick`, as the nav items' does, so
 * an unsaved-changes guard still gets its say.
 */
function VersionCaption({
  collapsed,
  onNavClick,
}: {
  collapsed: boolean;
  onNavClick: (e: React.MouseEvent, path: string) => void;
}) {
  const theme = useTheme();
  const { version } = readBuildInfo();
  const known = version !== UNKNOWN;

  return (
    <Box
      component={Link}
      to="/about"
      onClick={(e: React.MouseEvent) => onNavClick(e, '/about')}
      aria-label={known ? `${version}, ${ABOUT_COPY.menuItem}` : ABOUT_COPY.menuItem}
      sx={{
        ...theme.typography.caption,
        ...numericSx,
        alignSelf: collapsed ? 'center' : 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        // The WCAG 2.5.8 minimum target, so the caption does not lean on the
        // spacing exception beside Help & support.
        minHeight: 24,
        px: '10px',
        borderRadius: 1,
        color: 'text.secondary',
        textDecoration: 'none',
        '&:hover': { color: 'text.primary', textDecoration: 'underline' },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: FOCUS_RING(theme.palette.primary.main),
        },
      }}
    >
      {known ? version : ABOUT_COPY.sidebarCaption}
    </Box>
  );
}

function DrawerContent({ collapsed, navSections, onCollapseToggle, onNavClick }: DrawerContentProps) {
  const location = useLocation();
  const privacyNoticeUrl = usePrivacyNotice();
  const theme = useTheme();
  const focusRing = FOCUS_RING(alpha(theme.palette.primary.main, 0.35));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: collapsed ? '10px' : '12px', pt: '14px' }}>
        {navSections.map((section) => (
          <Box key={section.group} sx={{ mb: '6px' }}>
            {!collapsed && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: theme.palette.ink.faint,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                  px: '10px',
                  pt: '12px',
                  pb: '6px',
                  userSelect: 'none',
                }}
              >
                {section.label}
              </Typography>
            )}

            <List disablePadding>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                const button = (
                  <ListItem key={item.id} disablePadding sx={{ mb: '2px' }}>
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      onClick={(e: React.MouseEvent) => onNavClick(e, item.path)}
                      aria-current={isActive ? 'page' : undefined}
                      sx={{
                        position: 'relative',
                        borderRadius: 1,
                        py: '7px',
                        px: '10px',
                        minHeight: 36,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        bgcolor: isActive ? theme.palette.accent.tint : 'transparent',
                        color: isActive ? 'primary.main' : 'text.secondary',
                        fontWeight: isActive ? 600 : 500,
                        '&:hover': {
                          bgcolor: isActive
                            ? theme.palette.accent.tint
                            : alpha(theme.palette.primary.main, 0.04),
                        },
                        '&.Mui-focusVisible': {
                          outline: 'none',
                          boxShadow: focusRing,
                        },
                      }}
                    >
                      {/* 2px left accent bar on active item */}
                      {isActive && (
                        <Box
                          component="span"
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            bottom: 6,
                            width: 2,
                            bgcolor: 'primary.main',
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                      )}

                      <ListItemIcon
                        sx={{
                          color: isActive ? 'primary.main' : 'text.secondary',
                          minWidth: collapsed ? 0 : 34,
                          justifyContent: 'center',
                          '& .MuiSvgIcon-root': { fontSize: 15 },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>

                      {!collapsed && (
                        <ListItemText
                          primary={item.label}
                          slotProps={{
                            primary: {
                              sx: {
                                fontSize: 12.5,
                                fontWeight: isActive ? 600 : 500,
                                letterSpacing: '-0.005em',
                                whiteSpace: 'nowrap',
                              },
                            },
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );

                return collapsed ? (
                  <Tooltip key={item.id} title={item.label} placement="right" arrow>
                    {button}
                  </Tooltip>
                ) : (
                  <React.Fragment key={item.id}>{button}</React.Fragment>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Bottom links */}
      <Box sx={{ px: collapsed ? '10px' : '12px', pb: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <BottomLink icon={<HelpIcon />} label="Help & support" href={SUPPORT_MAILTO} collapsed={collapsed} />
        {privacyNoticeUrl && (
          <BottomLink
            icon={<PrivacyNoticeIcon />}
            label={PRIVACY_NOTICE_LABEL}
            href={privacyNoticeUrl}
            collapsed={collapsed}
            external
          />
        )}
        <VersionCaption collapsed={collapsed} onNavClick={onNavClick} />
      </Box>

      {/* Collapse toggle (desktop only) */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <IconButton
          size="small"
          onClick={onCollapseToggle}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          sx={{ color: 'text.disabled' }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
    </Box>
  );
}

interface SidebarProps {
  isCollapsed: boolean;
  mobileOpen: boolean;
  activeDrawerWidth: number;
  navSections: NavSection[];
  onDrawerToggle: () => void;
  onCollapseToggle: () => void;
  onNavClick: (e: React.MouseEvent, path: string) => void;
}

export function Sidebar({
  isCollapsed,
  mobileOpen,
  activeDrawerWidth,
  navSections,
  onDrawerToggle,
  onCollapseToggle,
  onNavClick,
}: SidebarProps) {
  const theme = useTheme();
  const sidebarBg = theme.palette.surface.sidebar;
  const drawerContentProps = { navSections, onCollapseToggle, onNavClick };

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{ width: { sm: activeDrawerWidth }, flexShrink: { sm: 0 }, transition: 'width 0.2s ease-out' }}
    >
      {/* Mobile: temporary drawer (full height, slides in over content) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true, disablePortal: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: sidebarBg,
            borderRight: '1px solid',
            borderColor: 'divider',
            // Start below the fixed 56px AppBar so the first nav item clears the
            // top bar (the desktop permanent drawer already does this).
            top: '56px',
            height: 'calc(100% - 56px)',
          },
        }}
      >
        <DrawerContent collapsed={false} {...drawerContentProps} />
      </Drawer>

      {/* Desktop: permanent drawer, starts below the 56px AppBar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: activeDrawerWidth,
            transition: 'width 0.2s ease-out',
            overflowX: 'hidden',
            bgcolor: sidebarBg,
            borderRight: '1px solid',
            borderColor: 'divider',
            top: '56px',
            height: 'calc(100vh - 56px)',
          },
        }}
        open
      >
        <DrawerContent collapsed={isCollapsed} {...drawerContentProps} />
      </Drawer>
    </Box>
  );
}
